// src/pages/AttendanceEntry/components/AttendanceTable.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "antd";
import { saveAttendance } from "../attendanceEntryThunk";
import moment from "moment-timezone";
import "../index.css";
import { getAccessType } from "../../../lib/functions";
import { selectAuthInfo } from "../../Auth/authSelectors";

const AttendanceTable = () => {
  const dispatch = useDispatch();
  const { attendanceRecords, filters } = useSelector(
    (state) => state.attendance
  );
  const authInfo = useSelector(selectAuthInfo);
  // Annual Leave and Casual Leave are the same pool, labeled by country —
  // only one toggle column is shown, wired to the matching attendance value.
  const isIndia = authInfo?.country === "India";
  const casualOrAnnualLabel = isIndia ? "Casual Leave" : "Annual Leave";
  const casualOrAnnualValue = isIndia ? "casual_leave" : "annual_leave";
  let [access_type, access_type_set] = useState("");

  useEffect(() => {
    const access = getAccessType("attendance_entry");
    access_type_set(access);
  }, []);

  const totals = attendanceRecords.reduce(
    (acc, curr) => {
      if (curr.attendance === "present") acc.present += 1;
      if (curr.attendance === "absence") acc.absent += 1;
      if (curr.attendance === "leave_with_pay") acc.leaveWithPay += 1;
      if (curr.attendance === "leave_without_pay") acc.leaveWithoutPay += 1;
      return acc;
    },
    { present: 0, absent: 0, leaveWithPay: 0, leaveWithoutPay: 0 }
  );

  const columns = [
    { title: "No.", render: (_, __, index) => index + 1 },
    { title: "Employee Name", dataIndex: "employee_name" },
    { title: "Department", dataIndex: "department_name" },
    { title: "Designation", dataIndex: "designation_name" },
    { title: "Contact", dataIndex: "contact_no" },
    {
      title: "Present",
      render: (_, record) => {
        const isYes =
          record.attendance === "present" &&
          (record.work_mode === "office" || !record.work_mode);

        if (access_type == "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }

        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: "present",
                  work_mode: "office",
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },
    {
      title: "WFH",
      render: (_, record) => {
        const isYes =
          record.attendance === "present" &&
          record.work_mode === "remote";

        if (access_type === "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }

        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: "present",
                  work_mode: "remote",   
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },


    {
      title: "Absent",
      render: (_, record) => {
        const isYes = record.attendance === "absence";
        if (access_type == "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }
        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: "absence",
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },
    {
      title: "Leave with Pay",
      render: (_, record) => {
        const isYes = record.attendance === "leave_with_pay";
        if (access_type == "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }
        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: "leave_with_pay",
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },
    {
      title: "Sick Leave",
      render: (_, record) => {
        const isYes = record.attendance === "sick_leave";
        if (access_type == "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }
        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: "sick_leave",
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },
    {
      title: casualOrAnnualLabel,
      render: (_, record) => {
        const isYes = record.attendance === casualOrAnnualValue;
        if (access_type == "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }
        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: casualOrAnnualValue,
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },
    {
      title: "Short Casual Leave",
      render: (_, record) => {
        const isYes = record.attendance === "short_casual_leave";
        if (access_type == "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }
        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: "short_casual_leave",
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },
    {
      title: "Short Sick Leave",
      render: (_, record) => {
        const isYes = record.attendance === "short_sick_leave";
        if (access_type == "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }
        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: "short_sick_leave",
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },
    {
      title: "Leave without Pay",
      render: (_, record) => {
        const isYes = record.attendance === "leave_without_pay";
        if (access_type == "READ") {
          return <span>{isYes ? "YES" : "NO"}</span>;
        }
        return (
          <span
            className={`attendance-cell ${
              isYes ? "attendance-yes" : "attendance-no"
            }`}
            onClick={() =>
              dispatch(
                saveAttendance({
                  ...record,
                  action: "leave_without_pay",
                  attendance_date: filters.attendanceDate,
                  year: filters.year,
                  month: filters.month,
                  month_day: moment(
                    filters.attendanceDate,
                    "YYYY-MM-DD"
                  ).daysInMonth(),
                })
              )
            }
          >
            {isYes ? "YES" : "NO"}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      <Table
        rowKey="employee_id"
        data-testid="attendance-table-wrapper"
        dataSource={attendanceRecords}
        columns={columns}
        pagination={false}
        bordered
        scroll={{ x: "max-content" }}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5} align="right">
              <b>Counting Total:</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="center">
              <b>{totals.present}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="center">
              <b>{totals.absent}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="center">
              <b>{totals.leaveWithPay}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={8} align="center">
              <b>{totals.leaveWithoutPay}</b>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default AttendanceTable;
